import { useState } from 'react'
import { Modal, Button, Form, Spinner } from 'react-bootstrap'
import { useMutation } from '@apollo/client'
import { toast } from 'react-toastify'
import { CREATE_TWILIO_WHATSAPP_TEMPLATE } from '../../gql/mutations/twilio'
import { WhatsAppPreview } from '../templates/components/tiptap/WhatsAppPreview'
import { WhatsAppEditor } from '../templates/components/tiptap/WhatsAppEditor'

export const CreateTwilioTemplateModal = ({
  show,
  onHide,
  onSuccess
}: {
  show: boolean
  onHide: () => void
  onSuccess: () => void
}) => {
  const [form, setForm] = useState({
    friendly_name: '',
    language: 'es',
    body_text: '',
    footer_text: ''
  })

  const [createTemplate, { loading }] = useMutation(CREATE_TWILIO_WHATSAPP_TEMPLATE)

  const handleChange = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  const handleCreate = async () => {
    if (!form.friendly_name || !form.body_text) {
      toast.warning('Internal Name and Body Text are required.')
      return
    }

    // Convert string keys like {{1}} format. For Twilio variables are typically {{1}}, {{2}}...
    // In this MVP we just pass text, Twilio infers variables from {{1}}.
    try {
      const { data } = await createTemplate({
        variables: {
          input: {
            friendly_name: form.friendly_name.toLowerCase().replace(/\s+/g, '_'),
            language: form.language,
            body_text: form.body_text,
            footer_text: form.footer_text || null
          }
        }
      })

      if (data?.createTwilioWhatsappTemplate?.success) {
        toast.success(data.createTwilioWhatsappTemplate.message)
        onSuccess()
        onHide()
        setForm({ friendly_name: '', language: 'en_US', body_text: '', footer_text: '' })
      } else {
        toast.error(data?.createTwilioWhatsappTemplate?.message || 'Failed to create template')
      }
    } catch (e: any) {
      toast.error(e.message || 'Critical error communicating with Twilio API')
    }
  }

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Create New WhatsApp Template (Twilio)</Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        <div className="row">
          <div className="col-md-7">
            <Form>
              <Form.Group className="mb-3">
                <Form.Label className="required">Internal Name</Form.Label>
                <Form.Control 
                  type="text" 
                  placeholder="e.g. payment_reminder_v2"
                  value={form.friendly_name}
                  onChange={(e) => handleChange('friendly_name', e.target.value)}
                />
                <Form.Text className="text-muted">Lowercase letters, numbers, and underscores only.</Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="required">Language</Form.Label>
                <Form.Select 
                  value={form.language}
                  onChange={(e) => handleChange('language', e.target.value)}
                >
                  <option value="es">Español (es)</option>
                  <option value="es_LA">Español Latinoamericano (es_LA)</option>
                  <option value="en">English (en)</option>
                  <option value="en_US">English US (en_US)</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="required">Body Text</Form.Label>
                <WhatsAppEditor
                  value={form.body_text}
                  onChange={(text) => handleChange('body_text', text)}
                  height={200}
                  placeholder="Hi {{Contact.first_name}}, your payment is due on {{1}}."
                  variables={['Contact.first_name', 'Contact.last_name', 'Contact.email', 'Input_text', 'Input_number', '1', '2', '3']}
                  getVariableText={(v) => `{{${v}}}`}
                />
                <Form.Text className="text-muted">
                    Use numbered variables (<code>&#123;&#123;1&#125;&#125;</code>) or named variables supported by your router. Basic markdown (*bold*, _italic_) and emojis are supported visually.
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Footer (Optional)</Form.Label>
                <Form.Control 
                  type="text" 
                  placeholder="Reply STOP to unsubscribe"
                  value={form.footer_text}
                  onChange={(e) => handleChange('footer_text', e.target.value)}
                  maxLength={60}
                />
              </Form.Group>

            </Form>
            
            <div className="alert alert-primary mt-4">
               <i className="bi bi-info-circle me-2 text-primary"></i>
               <strong>WhatsApp Approval:</strong> Upon creation, Twilio will automatically submit the template to WhatsApp for approval. You can sync the status later.
            </div>

          </div>
          <div className="col-md-5">
             <h6 className="fw-bolder mb-3 text-muted border-bottom pb-2">Preview (Approximate)</h6>
             <div className="d-flex justify-content-center">
               <WhatsAppPreview 
                  message={form.body_text} 
                  header={undefined} 
                  footer={form.footer_text} 
               />
             </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Cancel
        </Button>
        <Button variant="success" onClick={handleCreate} disabled={loading || !form.friendly_name || !form.body_text}>
          {loading ? <Spinner size="sm" animation="border" /> : 'Create and Submit for Approval'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
